// React core
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

// Redux
import { useDispatch, useSelector } from "react-redux"
import { setLoader, setOrders, setOrdersToAggregate } from "store/actions"

// Ant Design components
import {
	Button,
	Checkbox,
	Dropdown,
	Input,
	Menu,
	Modal,
	notification,
	Tag,
	Tooltip,
} from "antd"

// Project UI components
import { Icon, Table } from "components"

// Orders specific components
import { AggregationModal } from "./modals"

// Utilities and helpers
import Strings from "utils/strings"
import { ALL_CONTRACTS_VALUE } from "hooks"
import moment from "moment"
import { API, Endpoints } from "utils/api"
import { Duration } from "luxon"
import { getStatusLabel } from "../utils"
import StatusFilterDropdown, {
	OrderStatus,
	ORDER_STATUS_VALUES,
} from "./StatusFilterDropdown"
import OrderAttributeFilterDropdown, {
	GeoZone,
} from "./OrderAttributeFilterDropdown"
import ColumnSelectionDropdown, {
	DEFAULT_ORDER_COLUMNS,
	getVisibleColumnKeys,
	OrderColumnsPreference,
	useOrderColumnsPreference,
} from "./ColumnSelectionDropdown"
import { OrderFilters } from "../utils"
import { HOptionsType } from "components/Table"

export type { OrderStatus }
export { ORDER_STATUS_VALUES }

const ContentTable: React.FC<{
	orders: any[]
	pagination: any
	setPagination: any
	activeTab: string
	showImportModal: string
	setShowImportModal: (modal: string) => void
	setShowAddressValidationModal: (orderId?: any) => void
	selectedOrders: string[]
	setSelectedOrders: React.Dispatch<React.SetStateAction<string[]>>
	search: string
	setSearch: (search: string) => void
	defaultContracts: any[]
	defaultLots: any[]
	selectedStatus: OrderStatus[]
	setSelectedStatus: (status: OrderStatus[]) => void
	attributeFilters: OrderFilters
	setAttributeFilters: (value: OrderFilters) => void
	geoZones: GeoZone[]
	contractSelected: boolean
	refreshOrders?: () => void
}> = ({
	orders,
	pagination,
	setPagination,
	activeTab,
	setShowImportModal,
	setShowAddressValidationModal,
	selectedOrders,
	setSelectedOrders,
	search,
	setSearch,
	defaultContracts,
	defaultLots,
	selectedStatus,
	setSelectedStatus,
	attributeFilters,
	setAttributeFilters,
	geoZones,
	contractSelected,
	refreshOrders,
}) => {
	const dispatch = useDispatch()

	const {
		business: selectedBusiness,
		contract: rawSelectedContract,
		contractDetails: selectedContractDetails,
		date: selectedDate,
	} = useSelector((state: any) => state.filters) || {}
	const selectedContractType = selectedContractDetails?.type
	// "All contracts" is a Routes-only concept (the filter is shared across
	// screens) — this screen has no single contract to scope actions to.
	const selectedContract =
		rawSelectedContract === ALL_CONTRACTS_VALUE ? null : rawSelectedContract

	const temporaryOrders = useSelector((state: any) => state.temporaryOrders)
	const ordersToAggregate = useSelector(
		(state: any) => state.ordersToAggregate || []
	)
	const pageSize = useSelector((state: any) => state.pageSize)

	const [showPackingConfirmModal, setShowPackingConfirmModal] = useState(false)
	const [showBoxConfirmModal, setShowBoxConfirmModal] = useState(false)
	const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
	const [showAggregationModal, setShowAggregationModal] = useState(false)
	const [currentBoxData, setCurrentBoxData] = useState<any>(null)

	// User-chosen column order and visibility, persisted in localStorage
	const [columnsPreference, setColumnsPreference] = useOrderColumnsPreference()

	// Debounced search implementation
	const [localSearch, setLocalSearch] = useState(search)

	// Keep localSearch in sync if parent changes search (optional, for full sync)
	useEffect(() => {
		setLocalSearch(search)
	}, [search])

	// Debounce localSearch and update parent search after delay
	const debouncedSearch = useDebounce(localSearch, 800)

	useEffect(() => {
		setSearch(debouncedSearch)
	}, [debouncedSearch, setSearch])

	// Helper function to combine ordersToAggregate with outOfFormat boxes
	const getCombinedTableData = () => {
		if (ordersToAggregate.length === 0 || activeTab !== "aggregation") {
			return orders
		}

		if (orders?.[0]?.contract?.functionalities?.showUntreatedBoxes) {
			return ordersToAggregate
		}

		// Make all outOfFormat boxes into bags of a special "outOfFormat" box
		const outOfFormatBoxes = [] as any[]

		orders.forEach((order: any) => {
			const boxes = order.orderFlow?.boxes || []
			const filteredBoxes = boxes.filter((box: any) => box.id.startsWith("X"))

			outOfFormatBoxes.push(
				...filteredBoxes.map((box: any) => ({
					...box,
					flowId: order.orderFlow?._id,
					orderId: order._id,
				}))
			)
		})

		// Sort outOfFormatBoxes by box id
		outOfFormatBoxes.sort((a: any, b: any) => (a.id > b.id ? 1 : -1))

		// Create a "outOfFormat" box and add all outOfFormat boxes as separate bags
		const outOfFormatBox = {
			boxId: "outOfFormat",
			boxName: Strings.orders.outOfFormatBoxes,
			boxWeight: outOfFormatBoxes.length,
			boxes: outOfFormatBoxes,
			isOutOfFormat: true,
		}

		// Start with regular aggregated boxes
		const combinedData = [...ordersToAggregate, outOfFormatBox]

		return combinedData
	}

	// Memoized search handler - directly updates parent to trigger immediate search
	const handleSearch = useCallback(
		(value: string) => {
			setSearch(value)
		},
		[setSearch]
	)

	// Memoized header options
	const headerOptions = useTableHeaderOptions(
		setShowImportModal,
		selectedStatus,
		setSelectedStatus,
		handleSearch,
		setShowAggregationModal,
		temporaryOrders,
		refreshOrders,
		attributeFilters,
		setAttributeFilters,
		geoZones,
		contractSelected,
		columnsPreference,
		setColumnsPreference
	)

	// Memoize selectedOrders as a Set for O(1) lookups instead of O(n)
	const selectedOrdersSet = useMemo(
		() => new Set(selectedOrders),
		[selectedOrders]
	)

	// Memoize columns to prevent recreation on every render
	const columns = useMemo(
		() =>
			getTableColumns(
				dispatch,
				selectedBusiness,
				selectedContract,
				activeTab,
				selectedContractType,
				orders,
				ordersToAggregate,
				selectedOrders,
				setSelectedOrders,
				setCurrentBoxData,
				setShowBoxConfirmModal,
				columnsPreference
			),
		[
			dispatch,
			selectedBusiness,
			selectedContract,
			activeTab,
			selectedContractType,
			orders,
			ordersToAggregate,
			selectedOrders,
			setSelectedOrders,
			setCurrentBoxData,
			setShowBoxConfirmModal,
			columnsPreference,
		]
	)

	// Memoize actions to prevent recreation on every render
	const tableActions = useMemo(
		() => getTableActions(setSelectedOrders, setShowAddressValidationModal),
		[setSelectedOrders, setShowAddressValidationModal]
	)

	// Memoize onRowPress callback
	const handleRowPress = useCallback(
		(row: any, _event?: React.MouseEvent) => {
			const orderId = row.original?._id || row._id

			if (!setSelectedOrders) return

			setSelectedOrders((prev: string[]) => {
				if (prev.includes(orderId)) {
					// Deselect if already selected
					return prev.filter((id) => id !== orderId)
				} else {
					// Add to selection
					return [...prev, orderId]
				}
			})
		},
		[setSelectedOrders]
	)

	// Row styles: selection accentuates (darkens) error/warning colors rather than overriding them
	const getRowStyle = useCallback(
		({ original }: { original: any }) => {
			const isSelected = selectedOrdersSet.has(original._id)
			const hasErrors = Object.values(original.orderErrors || {}).some(Boolean)
			const hasWarnings =
				Object.values(original.orderWarnings || {}).some(Boolean) &&
				original.orderFlow.status === "address_confirmation"

			const colors = hasErrors
				? { bg: isSelected ? "#E8A5AA" : "#F8D7DA", border: "#DC3545" }
				: hasWarnings
					? { bg: isSelected ? "#F0D98C" : "#FFF3CD", border: "#FFB300" }
					: null

			if (colors) {
				return {
					backgroundColor: colors.bg,
					borderColor: colors.border,
					borderStyle: "solid",
					borderWidth: "1px",
					...(isSelected && { fontWeight: 500 }),
				}
			}

			return isSelected
				? { backgroundColor: "var(--background-selected)", fontWeight: 500 }
				: {}
		},
		[selectedOrdersSet]
	)

	// Memoize pagination API object
	const paginationApi = useMemo(
		() => ({
			pageSize: pageSize || 1000,
			total: pagination.total || orders.length,
			pageIndex: pagination.page || 0,
			setPage: (page: number, size: number) => {
				setPagination((prev: any) => ({
					...prev,
					pageSize: size,
					page: page,
				}))
			},
		}),
		[pageSize, pagination.total, pagination.page, orders.length, setPagination]
	)

	// Memoize filtration API object (search is now handled by custom header component)
	const filtrationApi = useMemo(
		() => ({
			showGlobalSearch: false,
			showColumnSearch: false,
		}),
		[]
	)

	// Memoize renderExpanded callback
	const renderExpanded = useCallback(
		(item: any) => {
			if (ordersToAggregate.length === 0) return null
			return renderAggregationBagInfo(
				dispatch,
				selectedBusiness,
				selectedContract,
				item,
				ordersToAggregate,
				orders
			)
		},
		[dispatch, selectedBusiness, selectedContract, ordersToAggregate, orders]
	)

	return (
		<div className="OrdersTableContainer">
			<Table
				// sortable={true}
				// defaultSortBy={[{ id: "status", desc: false }]}
				data={getCombinedTableData()}
				columns={columns}
				fullPage
				isSinglePage
				paginationApi={paginationApi}
				filtrationApi={filtrationApi}
				selectable
				filterable
				headerOptions={headerOptions}
				actions={tableActions}
				expanded={ordersToAggregate.length > 0}
				renderExpanded={renderExpanded}
				onRowPress={handleRowPress}
				getRowStyle={getRowStyle}
			/>

			{/* Packing Confirmation Modal */}
			{activeTab === "packing" &&
				renderPackingConfirmationModal(
					dispatch,
					orders,
					selectedBusiness,
					selectedContract,
					selectedOrders,
					showPackingConfirmModal,
					setSelectedOrders,
					setShowPackingConfirmModal
				)}

			{/* Box Confirmation Modal */}
			{showBoxConfirmModal &&
				renderBoxConfirmationModal(
					dispatch,
					selectedBusiness,
					selectedContract,
					currentBoxData,
					ordersToAggregate,
					showBoxConfirmModal,
					setShowBoxConfirmModal,
					orders
				)}

			{/* Delete Confirmation Modal */}
			{showDeleteConfirmModal &&
				renderDeleteConfirmationModal(
					dispatch,
					selectedOrders,
					orders,
					showDeleteConfirmModal,
					setShowDeleteConfirmModal,
					setSelectedOrders
				)}

			{/* Aggregation Modal */}
			<AggregationModal
				open={showAggregationModal}
				onClose={() => setShowAggregationModal(false)}
				business={selectedBusiness}
				contract={selectedContract}
				date={selectedDate}
				onFinalizeAggregation={refreshOrders}
			/>
		</div>
	)
}

export default ContentTable

function useDebounce(value: string, delay: number) {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

function renderPackingConfirmationModal(
	dispatch: any,
	orders: any[],
	business: any,
	contract: any,
	selectedOrders: string[],
	showPackingConfirmModal: boolean,
	setSelectedOrders: React.Dispatch<React.SetStateAction<string[]>>,
	setShowPackingConfirmModal: (show: boolean) => void
) {
	const orderCount = selectedOrders.length
	const pluralSuffix = orderCount !== 1 ? "s" : ""

	// Get selected orders data
	const selectedOrdersData = orders.filter((order) =>
		selectedOrders.includes(order._id)
	)

	// Check for warnings
	const today = moment().startOf("day")
	const currentTime = moment()
	const endOfDay = moment().endOf("day")
	const timeUntilEndOfDay = endOfDay.diff(currentTime, "hours", true)

	// Warning conditions
	const isLowOrderCount = orderCount <= 3
	const hasOldOrders = selectedOrdersData.some(
		(order) =>
			order.orderFlow?.deliveryDate &&
			moment.utc(order.orderFlow.deliveryDate).isBefore(today)
	)
	const isLateInDay = timeUntilEndOfDay < 4 // Less than 4 hours until end of day

	return (
		<Modal
			title={Strings.orders.confirmSendToOptimizer}
			visible={showPackingConfirmModal}
			onCancel={() => setShowPackingConfirmModal(false)}
			footer={[
				<Button key="cancel" onClick={() => setShowPackingConfirmModal(false)}>
					{Strings.generic.cancel}
				</Button>,
				<Button
					key="confirm"
					type="primary"
					onClick={async () => {
						await sendToOptimizer(
							dispatch,
							business,
							contract,
							selectedOrders,
							orders,
							setSelectedOrders
						)
						setShowPackingConfirmModal(false)
					}}
				>
					{Strings.orders.confirmSend}
				</Button>,
			]}
		>
			<div>
				{Strings.orders.confirmSendToOptimizerMessage
					.replace("{0}", orderCount.toString())
					.replace("{1}", pluralSuffix)}
			</div>
			<div>{Strings.orders.confirmSendToOptimizerDescription}</div>

			{/* Warning messages */}
			{(isLowOrderCount || hasOldOrders || isLateInDay) && (
				<div
					style={{
						marginTop: "16px",
						padding: "12px",
						backgroundColor: "#fff3cd",
						border: "1px solid #ffeaa7",
						borderRadius: "4px",
					}}
				>
					<div
						style={{
							fontWeight: "bold",
							color: "#856404",
							marginBottom: "8px",
						}}
					>
						<span role="img" aria-label="Warning">
							⚠️
						</span>{" "}
						Warnings:
					</div>
					{isLowOrderCount && (
						<div style={{ color: "#856404", marginBottom: "4px" }}>
							• Low order count ({orderCount} order{pluralSuffix}). Consider
							waiting for more orders to optimize delivery routes.
						</div>
					)}
					{hasOldOrders && (
						<div style={{ color: "#856404", marginBottom: "4px" }}>
							• Date is in the past. Please verify if these orders are still
							valid and the chosen date is correct.
						</div>
					)}
					{isLateInDay && (
						<div style={{ color: "#856404", marginBottom: "4px" }}>
							• Late in the day ({timeUntilEndOfDay.toFixed(1)} hours
							remaining). Consider if there's enough time for processing and
							delivery.
						</div>
					)}
				</div>
			)}
		</Modal>
	)
}

function renderDeleteConfirmationModal(
	dispatch: any,
	selectedOrders: string[],
	orders: any[],
	showDeleteConfirmModal: boolean,
	setShowDeleteConfirmModal: (show: boolean) => void,
	setSelectedOrders: React.Dispatch<React.SetStateAction<string[]>>
) {
	const orderCount = selectedOrders.length
	const pluralSuffix = orderCount !== 1 ? "s" : ""

	return (
		<Modal
			title={
				<div style={{ display: "flex", alignItems: "center" }}>
					<Icon
						name="warning"
						style={{ color: "#ff4d4f", marginRight: 8, fontSize: 20 }}
					/>
					<span>Confirm Order Deletion</span>
				</div>
			}
			visible={showDeleteConfirmModal}
			onCancel={() => setShowDeleteConfirmModal(false)}
			footer={[
				<Button key="cancel" onClick={() => setShowDeleteConfirmModal(false)}>
					{Strings.generic.cancel}
				</Button>,
				<Button
					key="delete"
					type="primary"
					danger
					onClick={async () => {
						await handleOrderDeletion(dispatch, selectedOrders, orders)
						setSelectedOrders([]) // Clear selection after deletion
						setShowDeleteConfirmModal(false)
					}}
				>
					{`Delete ${orderCount} Order${pluralSuffix}`}
				</Button>,
			]}
		>
			<div>
				<div
					style={{
						padding: "12px",
						backgroundColor: "#fff1f0",
						border: "1px solid #ffccc7",
						borderRadius: "4px",
						marginBottom: "16px",
					}}
				>
					<div
						style={{
							fontWeight: "bold",
							color: "#cf1322",
							marginBottom: "8px",
						}}
					>
						<span role="img" aria-label="Warning">
							⚠️
						</span>{" "}
						Warning: This action cannot be undone!
					</div>
					<div style={{ color: "#8c8c8c", fontSize: "14px" }}>
						You are about to permanently delete{" "}
						<strong>
							{orderCount} order{pluralSuffix}
						</strong>
						. This will remove {orderCount === 1 ? "it" : "them"} from the
						system completely.
					</div>
				</div>

				<div style={{ marginBottom: "12px" }}>
					<strong>Orders to be deleted:</strong>
				</div>
				<div
					style={{
						maxHeight: "200px",
						overflowY: "auto",
						padding: "8px",
						backgroundColor: "#fafafa",
						border: "1px solid #d9d9d9",
						borderRadius: "4px",
					}}
				>
					{selectedOrders.map((orderId) => {
						const order = orders.find((o) => o._id === orderId)
						return (
							<div
								key={orderId}
								style={{
									padding: "4px 8px",
									marginBottom: "4px",
									backgroundColor: "#fff",
									border: "1px solid #e8e8e8",
									borderRadius: "2px",
									fontSize: "13px",
								}}
							>
								{order?.orderId || orderId}
							</div>
						)
					})}
				</div>
			</div>
		</Modal>
	)
}

function renderBoxConfirmationModal(
	dispatch: any,
	business: any,
	contract: any,
	boxData: any,
	ordersToAggregate: any[],
	showBoxConfirmModal: boolean,
	setShowBoxConfirmModal: (show: boolean) => void,
	orders: any[] = []
) {
	if (!boxData) return null

	const untreatedBags = boxData.bags?.filter((bag: any) => !bag.treated) || []
	const pluralSuffix = untreatedBags.length !== 1 ? "s" : ""

	// Check if this is the last box in aggregation
	const remainingBoxes = ordersToAggregate.filter(
		(box) => box.boxId !== boxData.boxId
	)
	const isLastBox = remainingBoxes.length === 0

	// Check for untreated outOfFormat boxes
	const outOfFormatBoxes: any[] = []
	orders.forEach((order: any) => {
		const boxes = order.orderFlow?.boxes || []
		const filteredBoxes = boxes.filter((box: any) => box.id.startsWith("X"))
		outOfFormatBoxes.push(...filteredBoxes)
	})
	const untreatedOutOfFormat = outOfFormatBoxes.filter((box) => {
		return box.treated !== true
	}).length
	const hasUntreatedOutOfFormat = untreatedOutOfFormat > 0
	const showUntreatedBoxes =
		orders?.[0]?.contract?.functionalities?.showUntreatedBoxes

	return (
		<Modal
			title={Strings.orders.confirmBoxCompletion}
			visible={showBoxConfirmModal}
			onCancel={() => setShowBoxConfirmModal(false)}
			footer={[
				<Button key="cancel" onClick={() => setShowBoxConfirmModal(false)}>
					{Strings.generic.cancel}
				</Button>,
				<Button
					key="confirm"
					type="primary"
					danger
					onClick={() => {
						aggregateBags(
							dispatch,
							business,
							contract,
							boxData.boxId,
							false,
							boxData,
							ordersToAggregate,
							false // Proceed with untreated bags
						)
						setShowBoxConfirmModal(false)
					}}
				>
					{Strings.orders.yesFinishBox}
				</Button>,
			]}
		>
			<div className="UploadModal">
				<div style={{ marginBottom: 16 }}>
					<Icon
						name="warning"
						style={{ color: "#ff4d4f", marginRight: 8, fontSize: 16 }}
					/>
					<strong>{Strings.orders.warningCannotUndo}</strong>
				</div>
				<div style={{ marginBottom: 16 }}>
					<p style={{ marginBottom: "12px" }}>
						This box has <strong>{untreatedBags.length}</strong> untreated
						package{pluralSuffix}.
					</p>
					<p style={{ marginBottom: "16px", color: "#666" }}>
						Are you sure you want to finish this box without treating all
						packages?
					</p>
					{isLastBox &&
						hasUntreatedOutOfFormat &&
						showUntreatedBoxes === false && (
							<div
								style={{
									padding: "12px",
									backgroundColor: "#fff7e6",
									border: "1px solid #ffec8c",
									borderRadius: "6px",
									marginBottom: "16px",
								}}
							>
								<div
									style={{
										fontSize: "12px",
										color: "#856404",
										marginBottom: "4px",
									}}
								>
									<Icon
										name="info"
										style={{ color: "#faad14", marginRight: 4 }}
									/>
									<strong>Additional Information:</strong>
								</div>
								<div style={{ fontSize: "12px", color: "#856404" }}>
									This is the last regular aggregation box. There are still{" "}
									<strong>
										{untreatedOutOfFormat} / {outOfFormatBoxes.length}
									</strong>{" "}
									untreated outOfFormat box
									{untreatedOutOfFormat !== 1 ? "es" : ""} that will remain
									untreated after aggregation.
								</div>
							</div>
						)}
				</div>
				<div
					style={{
						padding: "12px",
						backgroundColor: "#f5f5f5",
						borderRadius: "6px",
						border: "1px solid #d9d9d9",
					}}
				>
					<div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
						Box Information:
					</div>
					<div style={{ fontWeight: "bold", color: "#1890ff" }}>
						Box ID: {boxData.boxId}
					</div>
					<div style={{ fontSize: "12px", color: "#fa8c16", marginTop: "4px" }}>
						{untreatedBags.length} untreated / {boxData.bags?.length || 0} total
						packages
					</div>
				</div>
			</div>
		</Modal>
	)
}

function renderAggregationBagInfo(
	dispatch: any,
	business: any,
	contract: any,
	box: any,
	ordersToAggregate: any[],
	orders: any[]
) {
	const normalBagColumns = [
		{
			Header: Strings.orders.bagId,
			id: "bagId",
			Cell: (cell: any) => {
				let bag = cell.row?.original
				return (
					<Input
						id="drawerBagId"
						placeholder={Strings.orders.bagId}
						value={bag?.bagId || "-"}
						readOnly
					/>
				)
			},
		},
		{
			Header: Strings.orders.weight,
			id: "bagWeight",
			Cell: (cell: any) => {
				let bag = cell.row?.original
				return (
					<Input
						id="drawerBagWeight"
						placeholder={Strings.orders.weight}
						value={bag?.bagWeight || "-"}
						readOnly
					/>
				)
			},
		},
		{
			Header: Strings.orders.boxId,
			id: "boxId",
			Cell: (cell: any) => {
				let orderToAgg = ordersToAggregate.find(
					(box: any) => cell?.row?.original?.boxOrderId === box?._id
				)
				const [boxId, setBoxId] = useState(orderToAgg?.boxId || "")

				const aggregateBagsHandler = async (e: any) => {
					if (
						e.target.value === "" ||
						e.target.value === "N/D" ||
						e.target.value === "-" ||
						e.target.value === orderToAgg?.boxId
					) {
						return
					}
					await aggregateBags(
						dispatch,
						business,
						contract,
						e.target.value,
						true,
						orderToAgg,
						ordersToAggregate
					)
				}

				return (
					<Input
						value={boxId || ""}
						placeholder={"N/D"}
						onChange={(e: any) => {
							setBoxId(e.target.value)
						}}
						onBlur={async (e: any) => {
							aggregateBagsHandler(e)
						}}
						onKeyDown={(e: any) => {
							if (e.key === "Enter") {
								aggregateBagsHandler(e)
							}
						}}
					/>
				)
			},
		},

		{
			Header: Strings.orders.treated,
			id: "treated",
			Cell: (cell: any) => {
				let bag = cell.row.original
				let box = ordersToAggregate.find(
					(box: any) => bag.boxOrderId === box?._id
				)
				return (
					<Checkbox
						checked={bag?.treated || false}
						disabled={!box?.boxId}
						onClick={async (e: any) => {
							if (box._id) {
								await updateBag(
									dispatch,
									cell?.row?.original?.orderId,
									cell?.row?.original?.flowId,
									e.target.checked,
									ordersToAggregate
								)
							} else {
								notification.warn({
									message: Strings.sidebar.orders,
									description: Strings.orders.fillBagFields,
									placement: "bottomRight",
									duration: 5,
								})
							}
						}}
					/>
				)
			},
		},
	]

	const outOfFormatBagColumns = [
		{
			Header: Strings.orders.boxId,
			id: "boxId",
			Cell: (cell: any) => {
				let box = cell.row?.original
				return (
					<Input
						id="drawerBagId"
						placeholder={Strings.orders.boxId}
						value={box?.id || "-"}
						readOnly
					/>
				)
			},
		},
		{
			Header: Strings.orders.weight,
			id: "bagWeight",
			Cell: (cell: any) => {
				let box = cell.row?.original
				return (
					<Input
						id="drawerBagWeight"
						placeholder={Strings.orders.weight}
						value={box?.weight || "-"}
						readOnly
					/>
				)
			},
		},
		{
			Header: Strings.orders.treated,
			id: "treated",
			Cell: (cell: any) => {
				let box = cell.row.original
				return (
					<Checkbox
						checked={box?.treated || false}
						onClick={async (e: any) => {
							await updateOutOfFormatBox(
								dispatch,
								e.target.checked,
								cell?.row?.original?.orderId,
								cell?.row?.original?.flowId,
								box?._id,
								orders
							)
						}}
					/>
				)
			},
		},
	]

	return (
		<Table
			data={
				box?.original.isOutOfFormat ? box?.original?.boxes : box?.original?.bags
			}
			columns={
				box?.original.isOutOfFormat ? outOfFormatBagColumns : normalBagColumns
			}
			selectable
			paginated={false}
			disableOverflowY={true}
		/>
	)
}

async function updateBag(
	dispatch: any,
	orderId: any,
	flowId: any,
	treated: boolean,
	ordersToAggregate: any[]
) {
	dispatch(setLoader(true))
	try {
		const response = await API.put({
			url: Endpoints.uriOrders(orderId),
			data: {
				treated: treated,
				flowId: flowId,
			},
		})

		// Update bag in state with treated value
		if (response.ok) {
			// const updatedOrdersToAggregate = ordersToAggregate.map((order: any) => {
			// 	return {
			// 		...order,
			// 		bags: order.bags.map((bag: any) => {
			// 			if (bag.flowId === flowId) {
			// 				return { ...bag, treated: treated }
			// 			}
			// 			return bag
			// 		}),
			// 	}
			// })

			const updatedOrdersToAggregate = ordersToAggregate.map((order: any) => {
				let bagsChanged = false

				const newBags = order.bags.map((bag: any) => {
					if (bag.flowId === flowId && bag.treated !== treated) {
						bagsChanged = true
						return { ...bag, treated }
					}
					return bag
				})

				if (bagsChanged) {
					return { ...order, bags: newBags }
				}
				return order // return original object if nothing changed
			})
			dispatch(setOrdersToAggregate(updatedOrdersToAggregate))
		}
	} catch (err) {
		console.error("Error: ", err)
		notification.error({
			message: Strings.serverErrors.title,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	} finally {
		dispatch(setLoader(false))
		return
	}
}

async function updateOutOfFormatBox(
	dispatch: any,
	treated: boolean,
	orderId: string,
	flowId: string,
	boxId: string,
	orders: any[]
) {
	dispatch(setLoader(true))

	try {
		const response = await API.put({
			url: Endpoints.uriOrders(orderId),
			data: {
				outOfFormatTreated: treated,
				flowId: flowId,
				boxId: boxId,
			},
		})

		// Update bag in state with treated value
		if (response.ok) {
			const updatedOrder = response?.data?.results.order
			const updatedOrders = orders.map((order: any) =>
				order._id === updatedOrder._id ? updatedOrder : order
			)
			dispatch(setOrders(updatedOrders))
		}
	} catch (err) {
		console.error("Error: ", err)
		notification.error({
			message: Strings.serverErrors.title,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	} finally {
		dispatch(setLoader(false))
		return
	}
}

async function aggregateBags(
	dispatch: any,
	business: any,
	contract: any,
	boxId: any,
	registeredId: boolean,
	box: any,
	ordersToAggregate: any[],
	allowUntreatedBags: boolean = true
) {
	let ordersIds = [] as any

	if (box?.bags.length > 0) {
		for (let index = 0; index < box?.bags.length; index++) {
			const bag = box?.bags[index]
			if (!!registeredId) {
				ordersIds.push(bag.orderId)
			} else {
				if (!!bag.treated || allowUntreatedBags === false)
					ordersIds.push(bag.orderId)
			}
		}
	}

	dispatch(setLoader(true))

	const body = {
		business,
		contract,
		boxId,
		orders: ordersIds,
		registeredId,
	} as any

	let response: any
	try {
		response = await API.patch({
			url: Endpoints.uriAggregation(),
			data: body,
		})

		if (response.ok) {
			notification.success({
				message: Strings.sidebar.orders,
				description: response?.data?.message,
				placement: "bottomRight",
				duration: 5,
			})

			// Update the ordersToAggregate state with the new boxId
			if (registeredId) {
				const updatedOrdersToAggregate = ordersToAggregate.map((order: any) => {
					if (box.bags.some((bag: any) => bag.flowId === order.orderFlowId)) {
						return {
							...order,
							boxId: boxId,
							registeredId: registeredId,
						}
					}
					return order
				})

				dispatch(setOrdersToAggregate(updatedOrdersToAggregate))
			} else {
				// Remove the box from ordersToAggregate
				const updatedOrdersToAggregate = ordersToAggregate.filter(
					(order: any) => order._id !== box._id
				)
				dispatch(setOrdersToAggregate(updatedOrdersToAggregate))
			}
		}
	} catch (err) {
		console.log("🚀 . ContentTable.tsx:404 . err:", err)
		notification.error({
			message: Strings.serverErrors.title,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	} finally {
		dispatch(setLoader(false))
		return
	}
}

async function handleOrderDeletion(
	dispatch: any,
	orderIds: string[],
	allOrders: any[] = []
) {
	try {
		const response = await API.delete({
			url: Endpoints.uriOrders(),
			data: { orderIds },
		})
		if (response.ok) {
			// Remove deleted orders from state
			const filteredOrders = allOrders.filter(
				(order) => !orderIds.includes(order._id)
			)
			dispatch(setOrders(filteredOrders))

			// Show success notification
			notification.success({
				message: Strings.sidebar.orders,
				description: response?.data?.message,
				placement: "bottomRight",
				duration: 5,
			})
		} else {
			notification.error({
				message: Strings.sidebar.orders,
				description: response.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
	} catch (error) {
		notification.error({
			message: Strings.sidebar.orders,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	}
}

const getTableActions = (
	setSelectedOrders: React.Dispatch<React.SetStateAction<string[]>>,
	setShowAddressValidationModal: (orderId: string) => void = () => {}
) => {
	let actions = {} as any

	actions.edit = (row: any) => {
		return {
			onClick: () => {
				if (row.orderFlow.status === "address_confirmation") {
					setShowAddressValidationModal(row.orderId)
				} else {
					setSelectedOrders([row._id])
				}
			},
		}
	}

	actions.view = (row: any) => ({
		onClick: () => window.open(`/orders/${row._id}`, "_blank"),
	})

	return actions
}

// ***************************** \\
// Header Options for Orders Table \\
// ********************************* \\

const { Search } = Input

const REFRESH_COOLDOWN_S = 5

const useTableHeaderOptions = (
	setShowImportModal = (modal: string) => {},
	selectedStatus: OrderStatus[] = [],
	setSelectedStatus: (status: OrderStatus[]) => void = () => {},
	onSearch: (value: string) => void = () => {},
	setShowAggregationModal: (show: boolean) => void = () => {},
	temporaryOrders: any[] = [],
	refreshOrders: (() => void) | undefined = undefined,
	attributeFilters: OrderFilters = {},
	setAttributeFilters: (value: OrderFilters) => void = () => {},
	geoZones: GeoZone[] = [],
	contractSelected: boolean = false,
	columnsPreference: OrderColumnsPreference = DEFAULT_ORDER_COLUMNS,
	setColumnsPreference: (preference: OrderColumnsPreference) => void = () => {}
) => {
	const [cooldownSeconds, setCooldownSeconds] = useState(0)
	const cooldownInterval = useRef<ReturnType<typeof setInterval> | null>(null)

	const handleRefresh = useCallback(() => {
		if (cooldownSeconds > 0 || !refreshOrders) return
		refreshOrders()
		setCooldownSeconds(REFRESH_COOLDOWN_S)
		cooldownInterval.current = setInterval(() => {
			setCooldownSeconds((prev) => {
				if (prev <= 1) {
					clearInterval(cooldownInterval.current!)
					cooldownInterval.current = null
					return 0
				}
				return prev - 1
			})
		}, 1000)
	}, [cooldownSeconds, refreshOrders])

	const orderGlobalInfo = useSelector((state: any) => state.orderGlobalInfo)

	const totalAggregation = orderGlobalInfo?.totalAggregation || 0
	const totalErrors = orderGlobalInfo?.totalErrors || 0
	const totalWarnings = orderGlobalInfo?.totalWarnings || 0

	const aggregationWarning = useMemo(() => {
		if (totalAggregation === 0) return null
		return (
			<Tooltip
				title={`${totalAggregation} ${Strings.orders.ordersToAggregate}`}
			>
				<Tag
					color="orange"
					style={{
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						gap: 4,
						fontSize: 13,
						padding: "4px 8px",
						height: "100%",
						marginRight: 0,
					}}
					onClick={() => setShowAggregationModal(true)}
				>
					<Icon name="package" /> {totalAggregation}
				</Tag>
			</Tooltip>
		)
	}, [totalAggregation, setShowAggregationModal])

	const validationWarning = useMemo(() => {
		const count = totalErrors + totalWarnings
		if (count === 0) return null
		return (
			<Tooltip title={`${count} ${Strings.orders.ordersToValidate}`}>
				<Tag
					color="red"
					style={{
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						gap: 4,
						fontSize: 13,
						padding: "4px 8px",
						height: "100%",
						marginRight: 0,
					}}
					onClick={() => {
						setSelectedStatus(["address_confirmation"] as OrderStatus[])
						onSearch("")
					}}
				>
					<Icon name="info-2" /> {count}
				</Tag>
			</Tooltip>
		)
	}, [totalErrors, totalWarnings, setSelectedStatus, onSearch])

	const [showTemporaryOrdersModal, setShowTemporaryOrdersModal] =
		useState(false)

	const temporaryOrdersWarning = useMemo(() => {
		const count = temporaryOrders?.length || 0
		if (count === 0) return null
		return (
			<>
				<Tooltip
					title={`${count} ${count === 1 ? Strings.orders.temporaryOrderPendingProcessing : Strings.orders.temporaryOrdersPendingProcessing}`}
				>
					<Tag
						color="gold"
						style={{
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							gap: 4,
							fontSize: 13,
							padding: "4px 8px",
							height: "100%",
							marginRight: 0,
						}}
						onClick={() => setShowTemporaryOrdersModal(true)}
					>
						<Icon name="clock1" /> {count}
					</Tag>
				</Tooltip>
				<Modal
					title={
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<Icon name="clock1" style={{ color: "#f39c12", fontSize: 18 }} />
							<span>
								{count === 1
									? Strings.orders.temporaryOrder
									: Strings.orders.temporaryOrders}{" "}
								({count})
							</span>
						</div>
					}
					visible={showTemporaryOrdersModal}
					onCancel={() => setShowTemporaryOrdersModal(false)}
					footer={null}
					width={500}
				>
					<div style={{ maxHeight: 480, overflowY: "auto" }}>
						{temporaryOrders.map((order: any, index: number) => (
							<div
								key={order.orderId || index}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									padding: "5px 0",
									borderBottom:
										index < temporaryOrders.length - 1
											? "1px solid #f0f0f0"
											: "none",
									flexWrap: "wrap" as const,
								}}
							>
								<span
									style={{
										fontFamily: "monospace",
										fontWeight: "bold",
										color: "#2980b9",
										fontSize: 13,
									}}
								>
									{order.orderId || "N/A"}
								</span>
								{order.lot && (
									<Tag color="default" style={{ margin: 0, fontSize: 12 }}>
										{Strings.orders.lot}: {order.lot}
									</Tag>
								)}
								{order.bagId && (
									<Tag color="green" style={{ margin: 0, fontSize: 12 }}>
										{Strings.orders.bagId}: {order.bagId}
									</Tag>
								)}
								{!order.bagId && order.boxes && order.boxes.length > 0 && (
									<Tag color="orange" style={{ margin: 0, fontSize: 12 }}>
										{Strings.orders.boxes}: {order.boxes.length}
									</Tag>
								)}
							</div>
						))}
					</div>
					<div
						style={{
							marginTop: 12,
							paddingTop: 8,
							borderTop: "1px solid #f0f0f0",
							fontSize: 12,
							color: "#7f8c8d",
							fontStyle: "italic",
							textAlign: "center",
						}}
					>
						{Strings.orders.temporaryOrdersFooter}
					</div>
				</Modal>
			</>
		)
	}, [temporaryOrders, showTemporaryOrdersModal])

	const statusFilterDropdown = useMemo(
		() => (
			<StatusFilterDropdown
				selectedStatus={selectedStatus}
				setSelectedStatus={setSelectedStatus}
			/>
		),
		[selectedStatus, setSelectedStatus]
	)

	const importButton = useMemo(
		() => (
			<Dropdown
				overlay={
					<Menu>
						<Menu.Item
							key="import-orders"
							icon={<Icon name="order" />}
							onClick={() => setShowImportModal("orders")}
						>
							<div>
								<div style={{ fontWeight: 500 }}>{Strings.sidebar.orders}</div>
								<div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
									{Strings.orders.importOrdersSubtitle}
								</div>
							</div>
						</Menu.Item>
						<Menu.Item
							key="import-boxes"
							icon={<Icon name="package" />}
							onClick={() => setShowImportModal("boxes")}
						>
							<div>
								<div style={{ fontWeight: 500 }}>
									{Strings.orders.importBoxesTitle}
								</div>
								<div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
									{Strings.orders.importBoxesSubtitle}
								</div>
							</div>
						</Menu.Item>
					</Menu>
				}
				trigger={["click"]}
			>
				<Button
					className="HeaderImportButton"
					style={{ display: "flex", alignItems: "center", gap: 4 }}
				>
					<Icon name="upload-folder" />
					<span className="HeaderImportButton__label">
						{Strings.generic.import}
					</span>
				</Button>
			</Dropdown>
		),
		[setShowImportModal]
	)

	const searchComponent = useMemo(
		() => (
			<Search
				className="HeaderSearchInput"
				placeholder={`${Strings.generic.search}...`}
				onSearch={onSearch}
				onBlur={(e) => onSearch(e.target.value)}
				allowClear
				maxLength={80}
			/>
		),
		[onSearch]
	)

	const warningsSection = useMemo(() => {
		if (!aggregationWarning && !validationWarning && !temporaryOrdersWarning)
			return null
		return (
			<div
				style={{ display: "flex", alignItems: "center", gap: 8, height: 40 }}
			>
				{aggregationWarning}
				{validationWarning}
				{temporaryOrdersWarning}
			</div>
		)
	}, [aggregationWarning, validationWarning, temporaryOrdersWarning])

	const attributeFilterDropdown = useMemo(
		() => (
			<OrderAttributeFilterDropdown
				filters={attributeFilters}
				setFilters={setAttributeFilters}
				geoZones={geoZones}
				contractSelected={contractSelected}
			/>
		),
		[attributeFilters, setAttributeFilters, geoZones, contractSelected]
	)

	const columnSelectionDropdown = useMemo(
		() => (
			<ColumnSelectionDropdown
				preference={columnsPreference}
				setPreference={setColumnsPreference}
			/>
		),
		[columnsPreference, setColumnsPreference]
	)

	const rightSection = useMemo(
		() => (
			<div className="HeaderRightSection">
				{importButton}
				{columnSelectionDropdown}
				{attributeFilterDropdown}
				{searchComponent}
			</div>
		),
		[
			importButton,
			columnSelectionDropdown,
			attributeFilterDropdown,
			searchComponent,
		]
	)

	return useMemo(
		() =>
			[
				{
					type: "custom" as const,
					icon: "filter",
					label: "status_filter",
					customComponent: statusFilterDropdown,
					marginRightAuto: true,
				},
				...(warningsSection
					? [
							{
								type: "custom" as const,
								icon: "warning",
								label: "warnings",
								customComponent: warningsSection,
								marginRightAuto: true,
							},
						]
					: []),
				{
					icon: "sync",
					tooltip: Strings.orders.refresh,
					noText: true,
					disabled: cooldownSeconds > 0,
					onClick: handleRefresh,
				},
				{
					type: "custom" as const,
					icon: "actions",
					label: "right_section",
					customComponent: rightSection,
				},
			] as HOptionsType[],
		[
			statusFilterDropdown,
			warningsSection,
			handleRefresh,
			cooldownSeconds,
			rightSection,
		]
	)
}

// Read an order's payment info (e.g. cash-on-delivery). An order "has payment"
// when it is explicitly required or carries a positive amount.
const getOrderPayment = (order: any) => {
	const p = order?.payment
	const hasPayment = p?.paymentRequired === true
	return { hasPayment, amount: p?.amount || 0, currency: p?.currency || "EUR" }
}

// TODO: Make this function more generic and reusable (Put into a utils file)
const translateOrderWarningAndError = (key: string) => {
	switch (key) {
		case "date":
			return Strings.orders.deliveryDate || key
		case "slot":
			return Strings.orders.slot || key
		case "postCode":
			return Strings.fields.zipCode || key
		case "address":
			return Strings.orders.address || key
		case "coordinates":
			return Strings.orders.coordinates || key
		case "phone":
			return Strings.orders.phoneNumber || key
		default:
			return key
	}
}

const getTableColumns = (
	dispatch: any,
	business: any,
	contract: any,
	tab: string,
	contractType: string,
	orders: any[],
	ordersToAggregate: any[],
	selectedOrders: string[],
	setSelectedOrders?: React.Dispatch<React.SetStateAction<string[]>>,
	setCurrentBoxData?: (data: any) => void,
	setShowBoxConfirmModal?: (show: boolean) => void,
	columnsPreference?: OrderColumnsPreference
): any[] => {
	const tabColumnMap: Record<
		string,
		(keyof ReturnType<typeof availableColumns>)[]
	> = {
		address_confirmation: [
			"selection",
			"orderId",
			"lot",
			"deliveryDate",
			"slot",
			"serviceType",
			"warnings",
			"errors",
			"clientName",
			"orderAddress",
			"formattedAddress",
			"latitude",
			"longitude",
		],
		picking: [
			"selection",
			"orderId",
			"lot",
			"deliveryDate",
			"slot",
			"serviceType",
			"clientName",
			"formattedAddress",
		],
		packing: [
			"selection",
			"orderId",
			"lot",
			"deliveryDate",
			"slot",
			"serviceType",
			"numberOfBoxes",
			"bagId",
			"numberOfItems",
			"totalWeight",
			"totalVolume",
		],
		aggregation:
			ordersToAggregate.length > 0
				? ["box", "boxId", "boxWeight", "packing"]
				: [
						"aggregationSelection",
						"orderId",
						"lot",
						"deliveryDate",
						"bagId",
						"slot",
						"numberOfBoxes",
						"numberOfItems",
						"totalWeight",
						"totalVolume",
					],
		inRoute: [
			"selection",
			"orderId",
			"status",
			"serviceType",
			"clientName",
			"formattedAddress",
		],
		finished: [
			"selection",
			"orderId",
			"status",
			"serviceType",
			"clientName",
			"deliveredDate",
			"arrivalTime",
			"departureTime",
		],
		test: [
			"selection",
			"orderId",
			"lot",
			"status",
			"payment",
			"deliveryDate",
			"slot",
			"clientName",
			"phoneNumber",
			"formattedAddress",
			"boxesOrBags",
		],
	}

	if (contractType !== "parcel") {
		// Remove bagId for non-parcel contracts
		tabColumnMap.packing = tabColumnMap.packing.filter(
			(column) => column !== "bagId"
		)
	}

	const columnsObj = availableColumns({
		tab,
		orders,
		selectedOrders,
		setSelectedOrders,
		dispatch,
		business,
		contractType,
		ordersToAggregate,
		setCurrentBoxData,
		setShowBoxConfirmModal,
	})
	// The row selection checkbox is not user-configurable, so it is always first;
	// the rest comes from the user's column preference (their order, their picks).
	let keys = (
		columnsPreference
			? ["selection", ...getVisibleColumnKeys(columnsPreference)]
			: tabColumnMap["test"] || []
	) as (keyof ReturnType<typeof availableColumns>)[]

	// Only show the payment column when at least one order carries payment.
	if (!orders.some((order) => getOrderPayment(order).hasPayment)) {
		keys = keys.filter((key) => key !== "payment")
	}

	return keys.map((key) => columnsObj[key])
}

type AvailableColumnsData = {
	tab: string
	orders: any[]
	selectedOrders: string[]
	setSelectedOrders?: React.Dispatch<React.SetStateAction<string[]>>
	dispatch?: any
	business?: any
	contract?: any
	contractType?: string
	ordersToAggregate?: any[]
	setCurrentBoxData?: (data: any) => void
	setShowBoxConfirmModal?: (show: boolean) => void
}

const availableColumns = (data?: AvailableColumnsData) => {
	const {
		orders = [],
		selectedOrders = [],
		setSelectedOrders = () => {},
		dispatch,
		business,
		ordersToAggregate = [],
		setCurrentBoxData,
		setShowBoxConfirmModal,
	} = data || {}
	return {
		selection: {
			id: "selection",
			fullCell: true,
			Header: () => {
				const ordersWithErrors = orders.filter((order) =>
					Object.values(order.orderErrors || {}).some(Boolean)
				)
				const ordersWithoutErrors = orders.filter(
					(order) => !Object.values(order.orderErrors || {}).some(Boolean)
				)

				const isPackingWithoutBagsOrBoxes = (order: any) =>
					order.orderFlow?.status === "packing" &&
					!order.orderFlow?.bagId &&
					!(order.orderFlow?.boxes || []).length

				const packingWithoutBagsOrBoxes = ordersWithoutErrors.filter(
					isPackingWithoutBagsOrBoxes
				)
				const cleanOrders = ordersWithoutErrors.filter(
					(order) => !isPackingWithoutBagsOrBoxes(order)
				)

				const allSelected =
					selectedOrders.length > 0 && selectedOrders.length === orders.length

				const allNonErroredSelected =
					ordersWithoutErrors.length > 0 &&
					selectedOrders.length === ordersWithoutErrors.length

				const allCleanSelected =
					cleanOrders.length > 0 && selectedOrders.length === cleanOrders.length

				const toggleSelectAll = () => {
					if (allSelected) {
						setSelectedOrders([])
					} else if (allNonErroredSelected && ordersWithErrors.length > 0) {
						// All non-errored selected → escalate to include errored orders
						setSelectedOrders(orders.map((order) => order._id))
					} else if (allCleanSelected && packingWithoutBagsOrBoxes.length > 0) {
						// All clean orders selected → escalate to include packing-without-bags (still no errors)
						setSelectedOrders(ordersWithoutErrors.map((order) => order._id))
					} else {
						// Default: select clean orders first
						if (cleanOrders.length > 0) {
							setSelectedOrders(cleanOrders.map((order) => order._id))
						} else if (ordersWithoutErrors.length > 0) {
							setSelectedOrders(ordersWithoutErrors.map((order) => order._id))
						} else {
							setSelectedOrders(orders.map((order) => order._id))
						}
					}
				}

				return (
					<Checkbox
						checked={allSelected}
						indeterminate={selectedOrders.length > 0 && !allSelected}
						onClick={toggleSelectAll}
					/>
				)
			},
			Cell: ({ row }: any) => {
				const order = row.original
				const status = order.orderFlow?.status
				const errors = order.orderErrors || {}
				const warnings = order.orderWarnings || {}
				const hasErrors = Object.values(errors).some(Boolean)
				const hasWarnings = Object.values(warnings).some(Boolean)

				if (status === "address_confirmation" && (hasErrors || hasWarnings)) {
					const isError = hasErrors
					const entries = Object.entries(isError ? errors : warnings)
					const activeEntries = entries.filter(([_, val]) => val)
					return (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Tooltip
								color="white"
								overlayInnerStyle={{
									padding: "12px 16px",
									borderRadius: "10px",
									boxShadow: "0 2px 12px rgba(0, 0, 0, 0.3)",
									maxWidth: "320px",
								}}
								title={
									<ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
										{activeEntries.map(([key], index) => (
											<li
												key={key}
												style={{
													display: "flex",
													alignItems: "center",
													marginBottom:
														index === activeEntries.length - 1 ? 0 : "8px",
													fontSize: "14px",
													lineHeight: "1.5",
													color: "#000000",
												}}
											>
												<span
													role="img"
													aria-label={isError ? "Error" : "Warning"}
													style={{ marginRight: 8 }}
												>
													{isError ? "❌" : "⚠️"}
												</span>
												{translateOrderWarningAndError(key)}
											</li>
										))}
									</ul>
								}
							>
								<Icon
									name="order"
									style={{
										color: isError ? "#DC3545" : "#FAAD14",
										fontSize: 20,
										marginLeft: 5,
										marginRight: 5,
										cursor: "pointer",
									}}
								/>
							</Tooltip>
						</div>
					)
				}

				let value = order.serviceType
				if (!value || value === "-") {
					value = "delivery"
				}

				const serviceTypes = Strings.orders.serviceTypes || {}
				return (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Tooltip
							title={
								<div
									style={{
										fontSize: "14px",
										color: "#000000",
										fontWeight: 500,
										textAlign: "center",
									}}
								>
									{(serviceTypes as Record<string, string>)[value] || value}
								</div>
							}
							color="white"
							overlayInnerStyle={{
								padding: "12px 16px",
								borderRadius: "10px",
								boxShadow: "0 2px 12px rgba(0, 0, 0, 0.3)",
								maxWidth: "320px",
							}}
							placement="top"
						>
							<Icon
								name={value === "delivery" ? "delivery" : "package"}
								style={{ fontSize: 18, color: "#1890ff", cursor: "pointer" }}
							/>
						</Tooltip>
					</div>
				)
				// const id = row.original._id
				// const isChecked = selectedOrders.includes(id)

				// const toggleRow = () => {
				// 	if (isChecked) {
				// 		setSelectedOrders(
				// 			selectedOrders.filter((orderId) => orderId !== id)
				// 		)
				// 	} else {
				// 		setSelectedOrders([...selectedOrders, id])
				// 	}
				// }

				// return <Checkbox checked={isChecked} onClick={toggleRow} />
			},
			maxWidth: 35,
			sortable: false,
		},
		orderId: {
			Header: Strings.orders.orderId,
			id: "orderId",
			accessor: (order: any) => order.orderId,
			// maxWidth: 150,
			minWidth: 120,
		},
		lot: {
			Header: Strings.orders.lot,
			id: "lot",
			accessor: (row: any) => row.lot || "-",
			maxWidth: 90,
		},
		status: {
			Header: Strings.orders.status,
			id: "status",
			maxWidth: 110,
			minWidth: 110,
			accessor: (row: any) => row.orderFlow?.status || "-",
			sortType: (rowA: any, rowB: any, columnId: string) => {
				const statusOrder = [
					"address_confirmation",
					"picking",
					"packing",
					"processing",
					"aggregation",
					"loading",
					"ready_to_van",
					"dispatching",
					"on_transit",
					"delivered",
					"not_delivered",
					"cancelled",
					"returned",
					"dispute",
					"billing",
				]
				const statusA = rowA.values[columnId] || ""
				const statusB = rowB.values[columnId] || ""
				const indexA = statusOrder.indexOf(statusA)
				const indexB = statusOrder.indexOf(statusB)
				// Unknown statuses go to the end
				const orderA = indexA === -1 ? statusOrder.length : indexA
				const orderB = indexB === -1 ? statusOrder.length : indexB
				return orderA - orderB
			},
			Cell: (cell: any) => {
				let row = cell.row.original
				return (
					<div
						style={{
							color:
								row?.orderFlow?.status === "delivered"
									? "green"
									: row?.orderFlow?.status === "not_delivered" ||
										  row?.orderFlow?.status === "cancelled" ||
										  row?.orderFlow?.status === "returned" ||
										  row?.orderFlow?.status === "dispute"
										? "red"
										: "black",
							justifyContent: "center",
						}}
					>
						{getStatusLabel(row?.orderFlow?.status)}
					</div>
				)
			},
		},
		payment: {
			Header: () => (
				<div style={{ textAlign: "center", width: "100%" }}>
					{Strings.orders.payment}
				</div>
			),
			id: "payment",
			accessor: (row: any) => getOrderPayment(row).amount,
			Cell: ({ row }: any) => {
				const { hasPayment, amount, currency } = getOrderPayment(row.original)
				if (!hasPayment) return <div style={{ textAlign: "center" }}>-</div>
				const label = amount > 0 ? `${amount.toFixed(2)} ${currency}` : "-"
				return (
					<div style={{ textAlign: "center" }}>
						<Tag color="gold">{label}</Tag>
					</div>
				)
			},
			maxWidth: 120,
			minWidth: 90,
		},
		deliveryDate: {
			Header: Strings.orders.deliveryDate,
			id: "deliveryDate",
			accessor: (row: any) =>
				(row.orderFlow?.deliveryDate &&
					moment.utc(row.orderFlow.deliveryDate).format("DD-MM-YYYY")) ||
				"-",
			maxWidth: 115,
		},
		slot: {
			Header: Strings.orders.deliverySlot,
			id: "slot",
			accessor: (row: any) => {
				if (row.slot?.from && row.slot?.to) {
					return `${Duration.fromMillis(row.slot.from).toFormat(
						"hh:mm"
					)} - ${Duration.fromMillis(row.slot.to).toFormat("hh:mm")}`
				}

				return "-"
			},
			minWidth: 110,
			maxWidth: 110,
		},
		warnings: {
			id: "warnings",
			Header: () => (
				<div style={{ textAlign: "center", width: "100%" }}>
					{Strings.orders.warnings}
				</div>
			),
			accessor: (order: any) => order.orderWarnings,
			Cell: ({ cell }: any) => {
				const warnings = cell.value
				const hasWarnings = warnings && Object.values(warnings).some(Boolean)

				if (!hasWarnings) return null
				return (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Tooltip
							color="white"
							overlayInnerStyle={{
								padding: "12px 16px",
								borderRadius: "10px",
								boxShadow: "0 2px 12px rgba(0, 0, 0, 0.3)", // soft shadow
								maxWidth: "320px",
							}}
							title={
								<ul
									style={{
										margin: 0,
										padding: 0,
										listStyle: "none",
									}}
								>
									{Object.entries(warnings)
										.filter(([_, val]) => val)
										.map(([key], index) => (
											<li
												key={key}
												style={{
													display: "flex",
													alignItems: "center",
													marginBottom:
														index === Object.keys(warnings).length - 1
															? 0
															: "8px",
													fontSize: "14px",
													lineHeight: "1.5",
													color: "#000000", // dark amber text
												}}
											>
												<span
													role="img"
													aria-label="Warning"
													style={{ marginRight: 8 }}
												>
													⚠️
												</span>
												{translateOrderWarningAndError(key)}
											</li>
										))}
								</ul>
							}
						>
							<Icon
								name="order"
								style={{
									color: "#FAAD14", // modern amber tone
									fontSize: 20,
									marginLeft: 5,
									marginRight: 5,
									cursor: "pointer",
								}}
							/>
						</Tooltip>
					</div>
				)
			},
			maxWidth: 80,
		},
		errors: {
			id: "errors",
			Header: () => (
				<div style={{ textAlign: "center", width: "100%" }}>
					{Strings.orders.errors}
				</div>
			),
			accessor: (order: any) => order.orderErrors,
			Cell: ({ cell }: any) => {
				const errors = cell.value
				const hasErrors = errors && Object.values(errors).some(Boolean)

				if (!hasErrors) return null
				return (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Tooltip
							color="white"
							overlayInnerStyle={{
								padding: "12px 16px",
								borderRadius: "10px",
								boxShadow: "0 2px 12px rgba(0, 0, 0, 0.3)", // soft shadow
								maxWidth: "320px",
							}}
							title={
								<ul
									style={{
										margin: 0,
										padding: 0,
										listStyle: "none",
									}}
								>
									{Object.entries(errors)
										.filter(([_, val]) => val)
										.map(([key], index) => (
											<li
												key={key}
												style={{
													display: "flex",
													alignItems: "center",
													marginBottom:
														index === Object.keys(errors).length - 1
															? 0
															: "8px",
													fontSize: "14px",
													lineHeight: "1.5",
													color: "#000000",
												}}
											>
												<span
													role="img"
													aria-label="Error"
													style={{ marginRight: 8 }}
												>
													❌
												</span>
												{translateOrderWarningAndError(key)}
											</li>
										))}
								</ul>
							}
						>
							<Icon
								name="order"
								style={{
									color: "#DC3545", // modern amber tone
									fontSize: 20,
									marginLeft: 5,
									marginRight: 5,
									cursor: "pointer",
								}}
							/>
						</Tooltip>
					</div>
				)
			},
			maxWidth: 80,
		},
		senderName: {
			Header: Strings.orders.senderName,
			id: "senderName",
			accessor: (row: any) => row.sender?.name || "-",
			maxWidth: 300,
			minWidth: 130,
			multiline: true,
		},
		clientName: {
			Header: Strings.orders.clientName,
			id: "clientName",
			accessor: (row: any) => row.user || "-",
			maxWidth: 300,
			minWidth: 130,
			multiline: true,
		},
		phoneNumber: {
			Header: Strings.orders.phoneNumber,
			id: "phoneNumber",
			accessor: (row: any) => row.phone || "-",
			maxWidth: 300,
			minWidth: 130,
			multiline: true,
		},
		orderAddress: {
			Header: Strings.orders.orderAddress,
			id: "orderAddress",
			accessor: (row: any) => row.orderAddress?.address || "-",
			multiline: true,
			minWidth: 200,
		},
		formattedAddress: {
			Header: Strings.orders.formattedOrderAddress,
			id: "formattedAddress",
			accessor: (row: any) => row.formattedAddress?.address || "-",
			multiline: true,
			minWidth: 200,
		},
		latitude: {
			Header: Strings.orders.latitude,
			id: "latitude",
			accessor: (row: any) =>
				row.userAddress?.location?.lat?.toFixed(7) ||
				row.orderAddress?.location?.lat?.toFixed(7) ||
				"-",
			multiline: true,
			maxWidth: 115,
		},
		longitude: {
			Header: Strings.orders.longitude,
			id: "longitude",
			accessor: (row: any) =>
				row.userAddress?.location?.lng?.toFixed(7) ||
				row.orderAddress?.location?.lng?.toFixed(7) ||
				"-",
			multiline: true,
			maxWidth: 115,
		},
		deliveredDate: {
			Header: Strings.orders.deliveredDate,
			id: "deliveredDate",
			accessor: (row: any) =>
				row.orderFlow?.deliveredDate
					? moment.utc(row.orderFlow.deliveredDate).format("DD-MM-YYYY")
					: "-",
			maxWidth: 115,
		},
		arrivalTime: {
			Header: Strings.orders.arrivalTime,
			id: "arrivalTime",
			accessor: (row: any) =>
				row.arrivalTime ? moment.utc(row.arrivalTime).format("HH:mm") : "-",
			maxWidth: 135,
			minWidth: 135,
			Cell: ({ cell }: any) => {
				return <div style={{ textAlign: "center" }}>{cell.value}</div>
			},
		},
		departureTime: {
			Header: Strings.orders.departureTime,
			id: "departureTime",
			accessor: (row: any) =>
				row.departureTime ? moment.utc(row.departureTime).format("HH:mm") : "-",
			maxWidth: 115,
			minWidth: 115,
			Cell: ({ cell }: any) => {
				return <div style={{ textAlign: "center" }}>{cell.value}</div>
			},
		},
		serviceType: {
			Header: () => (
				<div style={{ textAlign: "center", width: "100%" }}>
					{Strings.orders.serviceType}
				</div>
			),
			id: "serviceType",
			accessor: (row: any) => row.serviceType || "-",
			maxWidth: 120,
			minWidth: 120,
			Cell: ({ cell }: any) => {
				let value = cell.value
				if (!value || value === "-") {
					value = "delivery" // Default to delivery if no value
				}

				const serviceTypes = Strings.orders.serviceTypes || {}
				return (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Tooltip
							title={
								<div
									style={{
										fontSize: "14px",
										color: "#000000", // dark text
										fontWeight: 500,
										textAlign: "center",
									}}
								>
									{(serviceTypes as Record<string, string>)[value] || value}
								</div>
							}
							color="white"
							overlayInnerStyle={{
								padding: "12px 16px",
								borderRadius: "10px",
								boxShadow: "0 2px 12px rgba(0, 0, 0, 0.3)", // soft shadow
								maxWidth: "320px",
							}}
							placement="top"
						>
							<Icon
								name={value === "delivery" ? "delivery" : "package"}
								style={{ fontSize: 18, color: "#1890ff", cursor: "pointer" }}
							/>
						</Tooltip>
					</div>
				)
			},
		},

		// Packing specific columns
		numberOfBoxes: {
			Header: Strings.orders.totalBoxes,
			id: "numberOfBoxes",
			accessor: (order: any) => order.orderFlow?.boxes?.length || 0,
			maxWidth: 120,
			minWidth: 90,
		},
		numberOfItems: {
			Header: Strings.orders.itemsQuantity,
			id: "numberOfItems",
			accessor: (row: any) =>
				row?.itemsQuantity != null ? row.itemsQuantity : "-",
			maxWidth: 120,
			minWidth: 100,
		},
		totalWeight: {
			Header: Strings.orders.totalWeight,
			id: "totalWeight",
			accessor: (row: any) => {
				if (row.orderFlow?.bagId) {
					return row?.orderFlow?.bagWeight != null
						? `${row.orderFlow?.bagWeight} Kg`
						: "-"
				}

				if (row.orderFlow?.boxes?.length > 0) {
					const weight = row.orderFlow?.boxes.reduce(
						(acc: number, cur: any) => acc + cur.weight,
						0
					)
					return `${weight} Kg`
				}

				return "-"
			},
			maxWidth: 120,
			minWidth: 90,
		},
		totalVolume: {
			Header: Strings.orders.totalVolume,
			id: "totalVolume",
			accessor: (row: any) =>
				row?.totalWeight != null ? `${row.totalWeight} cm³` : "-",
			maxWidth: 120,
			minWidth: 100,
		},
		bagId: {
			Header: Strings.orders.bagId,
			id: "bagId",
			accessor: (row: any) => {
				return row?.orderFlow?.bagId || "-"
			},
			maxWidth: 150,
			minWidth: 120,
		},

		// Aggregation specific columns
		aggregationSelection: {
			id: "selection",
			Header: () => {
				let showCheckAllBox = orders?.find(
					(order: any) =>
						(!!order?.orderFlow?.bagId && !!order?.orderFlow?.bagWeight) ||
						order?.orderFlow?.boxes?.length > 0
				)

				let ordersToSelection = orders.filter((order: any) => {
					return (
						order?.orderFlow?.boxes?.length > 0 ||
						(order?.orderFlow?.bagId && order?.orderFlow?.bagId)
					)
				})
				let checked = selectedOrders?.length === ordersToSelection.length
				if (showCheckAllBox)
					return (
						<Checkbox
							checked={checked}
							onClick={() => {
								if (checked) {
									setSelectedOrders([])
								} else {
									const newSelectedOrders = ordersToSelection.map(
										(order: any) => order._id
									)
									setSelectedOrders(newSelectedOrders)
								}
							}}
						/>
					)
				return null
			},
			Cell: (cell: any) => {
				const id = cell.row?.original?._id || null

				const checked = selectedOrders.includes(id)

				// let actualOrder = selectedOrders?.find((order: any) => order._id === id)
				// let showCheckbox =
				// 	actualOrder?.orderFlow?.boxes?.length > 0 ||
				// 	(actualOrder?.orderFlow?.bagId && actualOrder?.orderFlow?.bagId)

				if (true) {
					return (
						<Checkbox
							checked={checked}
							disabled={false}
							onClick={() => {
								if (checked) {
									setSelectedOrders(
										selectedOrders.filter((orderId) => orderId !== id)
									)
								} else {
									setSelectedOrders([...selectedOrders, id])
								}
							}}
						/>
					)
				}
				return null
			},
			maxWidth: 35,
		},
		box: {
			Header: Strings.orders.box,
			id: "box",
			accessor: (row: any) => {
				return row?.boxName || "-"
			},
		},
		boxId: {
			Header: Strings.orders.boxId,
			id: "boxId",
			accessor: (row: any) => {
				if (row.isOutOfFormat) {
					return "N/A"
				}
				return row?.boxId || "N/D"
			},
		},
		boxWeight: {
			Header: Strings.orders.weight,
			id: "boxWeight",
			accessor: (row: any) => {
				return row?.boxWeight || "-"
			},
		},
		boxesOrBags: {
			id: "boxesOrBags",
			Header: () => (
				<div style={{ textAlign: "center", width: "100%" }}>
					{Strings.orders.boxes}
				</div>
			),
			accessor: (row: any) => ({
				boxes: row.orderFlow?.boxes || [],
				bagId: row.orderFlow?.bagId || null,
				bulkClassificationId:
					row.orderFlow?.classificationSource === "bulk"
						? row.orderFlow?.bulkClassificationId
						: null,
			}),
			Cell: ({ cell }: any) => {
				const { boxes, bagId, bulkClassificationId } = cell.value || {}
				const hasBoxes = boxes.length > 0
				const hasBag = !!bagId

				if (!hasBoxes && !hasBag)
					return <div style={{ textAlign: "center" }}>-</div>

				const boxIds = boxes.map((b: any) => b.id).join(", ")

				let tooltipText: string
				let iconName: string
				if (hasBoxes && hasBag) {
					tooltipText = `${boxIds} (${bagId})`
					iconName = "shopping-bag-thicker"
				} else if (hasBoxes) {
					tooltipText = boxIds
					iconName = "box"
				} else {
					tooltipText = bagId
					iconName = "shopping-bag-thicker"
				}
				if (bulkClassificationId) {
					tooltipText += ` · ${Strings.orders.bulkClassificationShort} ${String(
						bulkClassificationId
					)
						.slice(-6)
						.toUpperCase()}`
				}

				return (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Tooltip
							title={
								<div
									style={{
										fontSize: "14px",
										color: "#000000",
										fontWeight: 500,
										textAlign: "center",
									}}
								>
									{tooltipText}
								</div>
							}
							color="white"
							overlayInnerStyle={{
								padding: "8px 12px",
								borderRadius: "10px",
								boxShadow: "0 2px 12px rgba(0, 0, 0, 0.3)",
							}}
							placement="top"
						>
							<Icon
								name={iconName}
								style={{
									fontSize: 18,
									color: bulkClassificationId ? "#fa8c16" : "#1890ff",
									cursor: "pointer",
								}}
							/>
						</Tooltip>
					</div>
				)
			},
			maxWidth: 70,
			minWidth: 55,
		},

		packing: {
			Header: Strings.orders.packing,
			id: "packing",
			Cell: (cell: any) => {
				let order = cell.row?.original
				const contract = order?.contract
				const showUntreatedBoxes =
					contract?.showUntreatedBoxesInAggregation || false

				// Handle outOfFormat boxes - they don't have packing functionality
				if (order?.isOutOfFormat) {
					// Count treated out of format boxes
					const outOfFormatBoxes = order.boxes
					const treatedOutOfFormatBoxes = outOfFormatBoxes.filter(
						(box: any) => box.treated === true
					).length
					return treatedOutOfFormatBoxes + " / " + outOfFormatBoxes.length
				}

				// Handle regular aggregated boxes
				let checked = false
				let allTreated =
					order.bags?.every(function (obj: any) {
						return obj.treated === true
					}) || false

				let disabled = true

				if (showUntreatedBoxes === false) {
					const boxWasAssigned = !!cell.row.original.boxId
					disabled = !boxWasAssigned
				} else {
					disabled = !allTreated
				}

				return (
					<Checkbox
						checked={checked}
						disabled={disabled}
						onClick={() => {
							// If showUntreatedBoxes is false, allow aggregation finish with untreated but give warning
							// If showUntreatedBoxes is true, only allow aggregation finish when all bags are treated
							if (showUntreatedBoxes === false) {
								// Check if there are untreated bags
								const untreatedBags =
									order.bags?.filter((bag: any) => !bag.treated) || []

								const ordersHaveUntreatedOutOfFormat = orders.some((o: any) => {
									return o.orderFlow?.boxes?.some(
										(box: any) =>
											box.id.startsWith("X") && box.treated === false
									)
								})

								const showUntreatedBoxes =
									orders?.[0]?.contract?.functionalities?.showUntreatedBoxes

								const isLastBoxAndTheresUntreatedOutOfFormat =
									ordersToAggregate.length === 1 &&
									ordersHaveUntreatedOutOfFormat &&
									showUntreatedBoxes === false

								if (
									untreatedBags.length > 0 ||
									isLastBoxAndTheresUntreatedOutOfFormat
								) {
									// Show confirmation modal for untreated packages
									if (setCurrentBoxData && setShowBoxConfirmModal) {
										setCurrentBoxData(order)
										setShowBoxConfirmModal(true)
									}
								} else {
									// All bags are treated, proceed normally
									aggregateBags(
										dispatch,
										business,
										contract,
										order.boxId,
										false,
										order,
										ordersToAggregate
									)
								}
							} else {
								aggregateBags(
									dispatch,
									business,
									contract,
									order.boxId,
									false,
									order,
									ordersToAggregate
								)
							}
						}}
					/>
				)
			},
		},
	}
}

const sendToOptimizer = async (
	dispatch: any,
	business: string,
	contract: string,
	orderForOptimizer: string[],
	allOrders: any[],
	setSelectedOrders: (orders: string[]) => void
) => {
	if (orderForOptimizer.length === 0) return

	dispatch(setLoader(true))

	try {
		// TODO Call api but ignore response, change this when endpoint that does async processing is ready
		const apiPromise = API.patch({
			url: Endpoints.uriOrders("status"),
			data: {
				status: "packing",
				orders: orderForOptimizer,
				aggregationEnded: false,
			},
			headers: {
				business,
				contract,
			},
			axiosConfig: { timeout: 900000 }, // 15 minutes timeout
		})

		// Fake delay so user thinks the request is being processed
		// If it is too fast users might think nothing happened
		// Show fallback message after 2 seconds unless cancelled
		const fallbackTimeout = setTimeout(() => {
			notification.success({
				message: Strings.sidebar.orders,
				description: "Encomendas enviadas para o optimizador",
				placement: "bottomRight",
				duration: 5,
			})

			const filteredOrders = allOrders.filter((order: any) => {
				return !orderForOptimizer.includes(order._id)
			})
			dispatch(setOrders(filteredOrders))
			setSelectedOrders([]) // Clear selected orders after sending to optimizer

			// Reset the loader state
			dispatch(setLoader(false))
		}, 2000)

		// Handle actual API result
		apiPromise
			.then((response) => {
				// Cancel the fallback message
				if (!response.ok) {
					clearTimeout(fallbackTimeout)
					notification.error({
						message: Strings.sidebar.orders,
						description:
							response?.data?.message || Strings.serverErrors.wentWrong,
						placement: "bottomRight",
						duration: 5,
					})
				}
			})
			.catch((error) => {
				// Cancel fallback
				clearTimeout(fallbackTimeout)

				notification.error({
					message: Strings.sidebar.orders,
					description:
						error?.response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			})
			.finally(() => {
				// Reset the loader state
				dispatch(setLoader(false))
			})
	} catch (err) {
		notification.error({
			message: Strings.serverErrors.title,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
		console.error("Error sending orders to optimizer:", err)

		// Reset the loader state
		dispatch(setLoader(false))
	}
}
